import { useState, useRef, useEffect, useCallback } from 'react';
import { fmt$, fmt$0 } from '../utils/formatters';
import { categorizeLine, normalizeAlias } from '../lib/invoiceApi';
import { matchTokensAny } from '../utils/search';
import { supabase } from '../lib/supabase';
import IngredientForm from './IngredientForm';
import Modal from './Modal';
import { parsePackSize, guessPackType } from '../utils/packSize';
import { PackCells, PackGroupHeader, PackSubHeaders } from './PackCells';

// Thresholds
const RECONCILE_THRESHOLD = 0.50; // $ difference before blocking confirm
const RECONCILE_LARGE_GAP = 5;    // $ or 2% of total — above this, override requires typed confirmation
const PRICE_FLAG_YELLOW = 15;     // % change for yellow flag
const PRICE_FLAG_RED = 50;        // % change for red flag
const PRICE_CHANGE_THRESHOLD = 2; // % change before showing comparison row

function priceFlag(pctChange) {
  const abs = Math.abs(pctChange);
  if (abs >= PRICE_FLAG_RED) return 'red';
  if (abs >= PRICE_FLAG_YELLOW) return 'yellow';
  return null;
}

function lineZeroAmount(item) {
  return (item.amount === 0 || !item.amount) && (item.qty_shipped || 0) > 0 && (item.unit_price || 0) > 0;
}

const EC_OPTS=["Food","Beverage","Liquor","Beer","Wine","Packaging","Supplies","Retail","Services","Fees","Other"];
const COGS_SET=new Set(['Food','Beverage','Liquor','Beer','Wine','Packaging']);

export default function InvoiceReview({extracted,matched,setMatched,priceUpdates,setPriceUpdates,newItemEC,setNewItemEC,newItemCat,setNewItemCat,suggestEC,onConfirm,onCancel,onDefer,onReject,t,si,sb,sh,sd,mn,error,ings,setIngs,settings,save,orgId,uid,queueMode}){
const priceChanges=matched.map((item,idx)=>({item,idx})).filter(x=>x.item.price_changed);
const[ingCatOptions,setIngCatOptions]=useState([]);
useEffect(()=>{if(!orgId)return;supabase.from('ingredient_categories').select('name,is_visible').eq('org_id',orgId).order('sort_order').then(({data})=>{if(data)setIngCatOptions(data.filter(r=>r.is_visible!==false).map(r=>r.name));});},[orgId]);
const[editIngItem,setEditIngItem]=useState(null);
const[saving,setSaving]=useState(false);
const[editedTotal,setEditedTotal]=useState(extracted.grand_total);
const[reconcileOverride,setReconcileOverride]=useState(false);
const[reconcileTyped,setReconcileTyped]=useState('');
function isExtraCharge(item){
  const cat=(item.category||'').toLowerCase();
  const num=(item.item_number||'').toLowerCase();
  const desc=(item.description||'').toLowerCase();
  if(['tax','bottle_deposit','bottle deposit','discount','discounts'].includes(cat))return true;
  if(['tax','bottle_deposit','discount'].includes(num))return true;
  if(extracted.freight>0){
    if(cat==='freight'||num==='freight')return true;
    if(desc.includes('fuel surcharge')||desc.includes('freight')||desc.includes('shipping'))return true;
  }
  if(desc.includes('bottle deposit'))return true;
  if(desc.includes('discount')||desc.includes('promo')||desc.includes('coupon')||desc.includes('credit')||desc.includes('adjustment')||desc.includes('allowance'))return true;
  return false;
}
const lineSum=matched.filter(i=>i.amount&&i.amount>0&&!isExtraCharge(i)).reduce((s,i)=>s+(i.amount||0),0);
const extras=(extracted.tax||0)+(extracted.freight||0)+(extracted.bottle_deposit||0);
const discount=Number(extracted.discount)||0;
const expectedTotal=lineSum+extras-discount;
const activeTotal=parseFloat(editedTotal);
const totalValid=!isNaN(activeTotal)&&activeTotal>0;
const mathDiff=totalValid?Math.abs(expectedTotal-activeTotal):0;
const hasMathIssue=totalValid&&mathDiff>RECONCILE_THRESHOLD;
const isLargeGap=hasMathIssue&&(mathDiff>RECONCILE_LARGE_GAP||mathDiff/activeTotal>0.02);
const overrideValid=isLargeGap?reconcileTyped.trim().toUpperCase()==='CONFIRM':reconcileOverride;
const confirmBlocked=!totalValid||( hasMathIssue&&!overrideValid);
const[editingCell,setEditingCell]=useState(null);
const[editValue,setEditValue]=useState('');
const[lineEdits,setLineEdits]=useState({});
const[reassignRow,setReassignRow]=useState(null);
const[reassignSearch,setReassignSearch]=useState('');
const reassignRef=useRef(null);
const confirmBtnRef=useRef(null);
const unresolvedRefs=useRef({});
const[approvedNewItems,setApprovedNewItems]=useState(false);
const[skippedLines,setSkippedLines]=useState(new Set());

// Unmatched COGS lines that need resolution (not yet matched, not skipped)
const unresolvedLines=matched.map((item,idx)=>({item,idx})).filter(({item,idx})=>{
  const ig=item.matched_ingredient_id?(ings||[]).find(x=>x.id===item.matched_ingredient_id):null;
  if(ig)return false;
  if(skippedLines.has(idx))return false;
  const ec=newItemEC[idx]||suggestEC(item.description,item.category);
  return COGS_SET.has(ec);
});
const hasUnresolved=unresolvedLines.length>0;
const allNewApproved=approvedNewItems||!hasUnresolved;

const isSaving=saving||queueMode?.confirming;
async function doConfirm(){
  if(confirmBlocked||!allNewApproved||isSaving||!onConfirm)return;
  setSaving(true);
  try{await onConfirm({editedTotal:activeTotal,reconcileOverride:hasMathIssue&&overrideValid,reconcileDiff:hasMathIssue?mathDiff:0,approvedNewItems,skippedLines:[...skippedLines],lineEdits});}
  finally{setSaving(false);}
}
const handleConfirmAction=useCallback(()=>{
  if(!confirmBlocked&&onConfirm&&!isSaving)doConfirm();
},[confirmBlocked,onConfirm,activeTotal,hasMathIssue,overrideValid,mathDiff,isSaving]);

useEffect(()=>{
  if(!queueMode)return;
  function handleKey(e){
    if(e.key==='Enter'&&(e.metaKey||e.ctrlKey)){
      e.preventDefault();
      handleConfirmAction();
    }
  }
  window.addEventListener('keydown',handleKey);
  return()=>window.removeEventListener('keydown',handleKey);
},[queueMode,handleConfirmAction]);

function reassignLine(rowIdx, ing) {
  const item = matched[rowIdx];
  const invoiceCasePrice = (item.amount - (item.line_tax || 0)) / (item.qty_shipped || 1);
  const sheetCasePrice = ing.cp;
  const pctDiff = sheetCasePrice > 0 ? ((invoiceCasePrice - sheetCasePrice) / sheetCasePrice * 100) : 0;
  const priceChanged = Math.abs(pctDiff) > 2;
  const sheetUnitCost = ing.cq > 0 ? sheetCasePrice / ing.cq : 0;
  const newUnitCost = ing.cq > 0 ? invoiceCasePrice / ing.cq : 0;

  const updated = [...matched];
  updated[rowIdx] = {
    ...item,
    matched_ingredient_id: ing.id,
    matched_name: ing.n,
    match_confidence: 1.0,
    match_method: 'manual',
    cross_distributor: false,
    price_changed: priceChanged,
    old_price: sheetCasePrice,
    new_price: invoiceCasePrice,
    old_unit_cost: sheetUnitCost,
    new_unit_cost: newUnitCost,
    pct_change: pctDiff,
    cq: ing.cq || 1,
    ppc: ing.ppc || 1,
    spp: ing.spp || ing.cq || 1,
  };
  setMatched(updated);
  setReassignRow(null);
  setReassignSearch('');

  if (orgId && item.description) {
    supabase.from('ingredient_aliases').upsert({
      org_id: orgId, ingredient_id: ing.id,
      alias: item.description,
      normalized_alias: normalizeAlias(item.description),
      source: 'manual',
    }, { onConflict: 'org_id,normalized_alias' })
    .then(({ error: e }) => { if (e) console.error('[InvoiceReview] Alias upsert error:', e); });
  }
  // Backfill VIN on the ingredient if it has no item number yet
  if (orgId && item.item_number && !ing.vin) {
    const vin = item.item_number.toUpperCase();
    supabase.from('ingredients').update({ vendor_item_number: vin }).eq('id', ing.id)
      .then(({ error: e }) => { if (e) console.error('[InvoiceReview] VIN backfill error:', e); });
    if (setIngs) setIngs(p => p.map(x => x.id === ing.id ? { ...x, vin } : x));
  }
}

function startEdit(rowIdx,field,currentVal){
  setEditingCell({row:rowIdx,field});
  setEditValue(String(currentVal||''));
}

function commitEdit(rowIdx,field){
  setEditingCell(null);
  const val=parseFloat(editValue);
  if(isNaN(val))return;
  const item=matched[rowIdx];
  const fieldKey=field==='qty_shipped'?'qty_shipped':field==='unit_price'?'unit_price':'amount';
  const oldVal=item[fieldKey]||0;
  if(val===oldVal)return;

  setLineEdits(prev=>{
    const rowEdits={...(prev[rowIdx]||{})};
    if(!rowEdits[fieldKey])rowEdits[fieldKey]={original:oldVal,current:val};
    else rowEdits[fieldKey]={...rowEdits[fieldKey],current:val};
    return{...prev,[rowIdx]:rowEdits};
  });

  const updated=[...matched];
  const newItem={...updated[rowIdx],[fieldKey]:val};

  if(field!=='amount'){
    newItem.amount=Math.round((newItem.qty_shipped||0)*(newItem.unit_price||0)*100)/100;
    const oldAmt=item.amount||0;
    if(newItem.amount!==oldAmt){
      setLineEdits(prev=>{
        const rowEdits={...(prev[rowIdx]||{})};
        if(!rowEdits.amount)rowEdits.amount={original:oldAmt,current:newItem.amount};
        else rowEdits.amount={...rowEdits.amount,current:newItem.amount};
        return{...prev,[rowIdx]:rowEdits};
      });
    }
  }

  updated[rowIdx]=newItem;
  setMatched(updated);
}

function handleKeyDown(e,rowIdx,field){
  if(e.key==='Enter'){e.preventDefault();commitEdit(rowIdx,field);}
  if(e.key==='Escape'){setEditingCell(null);}
}

function isEdited(rowIdx,field){
  return lineEdits[rowIdx]?.[field]&&lineEdits[rowIdx][field].original!==lineEdits[rowIdx][field].current;
}

function originalVal(rowIdx,field){
  return lineEdits[rowIdx]?.[field]?.original;
}

// Derive pack display values for a line
function getLinePack(item, i){
  const ig=item.matched_ingredient_id?ings?.find(x=>x.id===item.matched_ingredient_id):null;
  const isDefault=ig&&(ig.ppc||1)<=1&&(ig.spp||0)<=0;
  // Matched with real pack data: use ingredient's authoritative values
  if(ig&&!isDefault){
    return{qty:ig.ppc||1,type:ig.pt||'CS',size:ig.spp||null,unit:ig.cu||null,source:'ingredient'};
  }
  // Unmatched or matched-at-defaults: parse from OCR
  const pack=parsePackSize(item.pack_size,item.pack_qty);
  if(pack){
    return{qty:pack.ppc,type:guessPackType(item.unit),size:pack.spp,unit:pack.pu,source:'ocr'};
  }
  return{qty:1,type:guessPackType(item.unit),size:null,unit:null,source:'none'};
}

function unitCost(casePrice,qty,size){
  if(!size||!qty)return null;
  return casePrice/(qty*size);
}

// Compute what differs between invoice and ingredient for comparison row
function getDiffs(item, ig, pack){
  if(!ig)return null;
  const diffs={};
  // Price diff
  const pctChange=item.pct_change||0;
  if(Math.abs(pctChange)>PRICE_CHANGE_THRESHOLD)diffs.price=true;
  // Pack diff: compare OCR pack against ingredient pack
  const ocrPack=parsePackSize(item.pack_size,item.pack_qty);
  if(ocrPack){
    const isDefault=(ig.ppc||1)<=1&&(ig.spp||0)<=0;
    if(!isDefault){
      if(ocrPack.ppc!==(ig.ppc||1))diffs.pack=true;
      if(ocrPack.spp&&ig.spp&&Math.abs(ocrPack.spp-ig.spp)>0.01)diffs.pack=true;
    }
  }
  return Object.keys(diffs).length>0?diffs:null;
}

// Shared cell styles
const cs={padding:'4px 6px',fontSize:10,whiteSpace:'nowrap'};
const csm={...cs,fontFamily:mn};
const csd={...cs,color:t.td}; // dimmed for comparison row

return(<div>
{/* Invoice header */}
<div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:12,marginBottom:16}}>
{[["Vendor",extracted.vendor,t.bl],["Invoice #",extracted.invoice_number||"—",t.tm],["Date",extracted.invoice_date||"—",extracted.date_needs_review?t.ac:t.tm]].map(([l,v,c])=>
<div key={l} style={{background:t.sf,borderRadius:8,padding:"10px 12px"}}>
<div style={{fontSize:9,color:t.td,textTransform:"uppercase"}}>{l}</div>
<div style={{fontSize:14,fontWeight:600,color:c,marginTop:2}}>{v}</div>
{l==='Date'&&extracted.date_needs_review&&<div style={{fontSize:9,color:t.ac,fontWeight:600,marginTop:2}}>⚠ {extracted.date_needs_review==='no_date'?'No date found on invoice':extracted.date_needs_review==='future'?'Date is in the future':extracted.date_needs_review==='bad_year'?'Year looks wrong — please verify':'Date needs review'}</div>}
</div>)}
<div style={{background:t.sf,borderRadius:8,padding:"10px 12px"}}>
<div style={{fontSize:9,color:t.td,textTransform:"uppercase"}}>Total</div>
<div style={{display:'flex',alignItems:'center',gap:4,marginTop:2}}><span style={{fontSize:14,fontWeight:600,color:t.gn}}>$</span><input value={editedTotal} onChange={e=>{setEditedTotal(e.target.value);setReconcileOverride(false);setReconcileTyped('');}} style={{background:'transparent',border:`1px solid ${hasMathIssue?t.yl:t.bd}`,borderRadius:4,padding:'2px 6px',fontFamily:mn,fontSize:14,fontWeight:600,color:t.gn,width:90,textAlign:'right'}}/></div>
{activeTotal!==extracted.grand_total&&<div style={{fontSize:8,color:t.td,marginTop:1}}>OCR: ${fmt$(extracted.grand_total)}</div>}
</div>
</div>

{/* Reconciliation gate */}
{hasMathIssue&&<div style={{background:isLargeGap?'rgba(200,55,45,0.06)':'rgba(212,168,36,0.08)',border:`1px solid ${isLargeGap?t.ac:t.yl}`,borderRadius:8,padding:14,marginBottom:14}}>
<div style={{fontSize:13,fontWeight:600,color:isLargeGap?t.ac:t.yl,marginBottom:6}}>⚠️ Line items ${fmt$0(lineSum)}{extras>0?` + charges ${fmt$0(extras)}`:''}{discount>0?` − discount ${fmt$0(discount)}`:''} = ${fmt$0(expectedTotal)}. Total is ${fmt$0(activeTotal)}. <span style={{fontFamily:mn}}>${fmt$0(mathDiff)}</span> difference.</div>
<div style={{fontSize:11,color:t.td,marginBottom:8}}>Confirm is blocked until reconciled. Fix the total above, edit line items, or acknowledge below.</div>
{isLargeGap?<div>
<div style={{fontSize:11,color:t.ac,fontWeight:500,marginBottom:4}}>Large gap (${fmt$0(mathDiff)}) — type CONFIRM to acknowledge:</div>
<div style={{display:'flex',gap:8,alignItems:'center'}}><input value={reconcileTyped} onChange={e=>setReconcileTyped(e.target.value)} placeholder="Type CONFIRM" style={{...si,width:140,fontSize:12,textTransform:'uppercase'}}/>{overrideValid&&<span style={{fontSize:10,color:t.gn,fontWeight:600}}>Acknowledged</span>}</div>
</div>:
<label style={{display:'flex',alignItems:'center',gap:6,cursor:'pointer',fontSize:11,color:t.tm}}>
<input type="checkbox" checked={reconcileOverride} onChange={e=>setReconcileOverride(e.target.checked)}/>
I've verified — confirm despite ${fmt$0(mathDiff)} difference
</label>}
</div>}
{extracted.confidence==='low'&&extracted.note&&<div style={{background:"rgba(212,168,36,0.08)",border:`1px solid ${t.yl}`,borderRadius:8,padding:14,marginBottom:14}}>
<div style={{fontSize:12,fontWeight:600,color:t.yl}}>⚠️ Low confidence: {extracted.note}</div>
</div>}

{/* Price changes alert */}
{(()=>{
const invDate=extracted.invoice_date;
const actionable=priceChanges.filter(x=>{const ig2=ings?.find(z=>z.id===x.item.matched_ingredient_id);return !ig2?.lastPriceDate||invDate>=ig2.lastPriceDate;});
const historical=priceChanges.filter(x=>{const ig2=ings?.find(z=>z.id===x.item.matched_ingredient_id);return ig2?.lastPriceDate&&invDate<ig2.lastPriceDate;});
return<>
{actionable.length>0&&<div style={{background:"rgba(212,168,36,0.08)",border:`1px solid ${t.yl}`,borderRadius:8,padding:14,marginBottom:14}}>
<div style={{fontSize:13,fontWeight:600,color:t.yl,marginBottom:8}}>{actionable.length} Price Change{actionable.length>1?"s":""} Detected</div>
{actionable.map(x=>{
const item=x.item;const mi=x.idx;
const id=item.matched_ingredient_id;
const ig=ings?.find(z=>z.id===id);
const isCW=!!item.isCatchWeight;
const curCp=ig?.cp??item.old_price;
const curCq=ig?.cq??item.cq??1;
const curUnit=curCq>0?curCp/curCq:0;
// For catch-weight: compare per-unit prices (old_price and new_price are already per-unit from matching)
const compareOld=isCW?curUnit:curCp;
const compareNew=item.new_price;
const pctNow=compareOld>0?((compareNew-compareOld)/compareOld*100):0;
const changed=Math.abs(pctNow)>2;
const risky=item.cross_distributor||Math.abs(item.pct_change)>50;
const priceLabel=isCW?`/${ig?.cu||'lb'}`:'/cs';
return(<div key={`pc-${mi}`} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 0",borderBottom:`1px solid ${t.bd}`,position:'relative'}}>
<div>
<div style={{fontSize:12,fontWeight:500}}>{ig?.n||item.matched_name}{item.cross_distributor&&<span style={{fontSize:9,color:t.ac,marginLeft:6}}>different distributor</span>}{isCW&&<span style={{fontSize:8,padding:'1px 5px',borderRadius:3,background:'rgba(59,130,246,0.1)',color:t.bl,marginLeft:6}}>catch-weight</span>}</div>
<div style={{fontSize:10,color:t.td}}>{item.description}{isCW&&item.catch_weight!=null?` (${item.catch_weight} ${ig?.cu||'lb'})`:''}</div>
</div>
<div style={{display:"flex",alignItems:"center",gap:10}}>
<div style={{textAlign:"right"}}>
<div><span style={{fontFamily:mn,fontSize:11,color:t.td,textDecoration:"line-through"}}>${fmt$(compareOld)}{priceLabel}</span><span style={{fontFamily:mn,fontSize:11}}> → </span><span style={{fontFamily:mn,fontSize:12,fontWeight:600,color:changed?(compareNew>compareOld?t.ac:t.gn):t.td}}>${fmt$(compareNew)}{priceLabel}</span></div>
<div style={{fontSize:9,color:t.td}}>{isCW?`${item.catch_weight} ${ig?.cu||'lb'} @ $${compareNew.toFixed(4)} = $${fmt$(item.amount)}`:`unit: $${curUnit.toFixed(4)}${changed?` → $${(curCq>0?compareNew/curCq:0).toFixed(4)}`:''}`} <span style={{color:changed?(compareNew>compareOld?t.ac:t.gn):t.td}}>({pctNow>0?'+':''}{pctNow.toFixed(1)}%)</span></div>
</div>
<div style={{display:"flex",alignItems:"center",gap:6}}>
<button onClick={()=>{setReassignRow(reassignRow?.idx===mi&&reassignRow?.source==='box'?null:{idx:mi,source:'box'});setReassignSearch('');}} style={{background:'rgba(59,130,246,0.08)',border:`1px solid ${t.bl}`,color:t.bl,cursor:'pointer',fontSize:11,fontFamily:"'DM Sans',sans-serif",padding:'2px 8px',borderRadius:10}}>Change</button>
{ig&&<button onClick={()=>setEditIngItem({ig:{...ig,cp:item.new_price},invoiceItem:item})} style={{background:'none',border:'none',color:t.bl,cursor:'pointer',fontSize:9,fontFamily:"'DM Sans',sans-serif",padding:0}}>Edit</button>}
<label style={{display:"flex",alignItems:"center",gap:4,fontSize:10,color:t.tm,cursor:"pointer"}}>
<input type="checkbox" checked={priceUpdates[id]||false} onChange={e=>setPriceUpdates(p=>({...p,[id]:e.target.checked}))}/>
Update
</label>
{risky&&!priceUpdates[id]&&<span style={{fontSize:8,color:t.ac}}>verify first</span>}
</div>
</div>
{reassignRow?.idx===mi&&reassignRow?.source==='box'&&<div style={{position:'absolute',top:'100%',right:0,zIndex:20,background:t.cd,border:`1px solid ${t.bd}`,borderRadius:8,padding:8,width:260,boxShadow:'0 4px 12px rgba(0,0,0,0.15)'}}>
<input autoFocus value={reassignSearch} onChange={e=>setReassignSearch(e.target.value)} placeholder="Search ingredients..." name="reassign-lookup" autoComplete="chrome-off" data-1p-ignore data-lpignore="true" style={{...si,width:'100%',marginBottom:4,boxSizing:'border-box',fontSize:11}} onKeyDown={e=>{if(e.key==='Escape'){setReassignRow(null);setReassignSearch('');}}}/>
{reassignSearch.length>=2&&(()=>{const results=(ings||[]).filter(ig2=>ig2.active!==false&&matchTokensAny([ig2.n,ig2.b||'',ig2.cat||''],reassignSearch)).slice(0,8);return results.length?<div style={{maxHeight:180,overflow:'auto'}}>{results.map(ig2=><div key={ig2.id} onClick={()=>reassignLine(mi,ig2)} style={{padding:'5px 8px',cursor:'pointer',borderBottom:`1px solid ${t.bd}`,fontSize:11}} onMouseOver={e=>e.currentTarget.style.background=t.sf} onMouseOut={e=>e.currentTarget.style.background='transparent'}>
<div style={{fontWeight:500}}>{ig2.n}</div>
<div style={{fontSize:9,color:t.td}}>{ig2.b||'—'} · {ig2.cat||'—'} · {ig2.dist||'—'}</div>
</div>)}</div>:<div style={{fontSize:10,color:t.td,padding:4}}>No matches</div>;})()}
{reassignSearch.length<2&&<div style={{fontSize:10,color:t.td,padding:4}}>Type 2+ chars to search</div>}
</div>}
</div>);})}
</div>}
{historical.length>0&&<div style={{background:t.sf,border:`1px solid ${t.bd}`,borderRadius:8,padding:14,marginBottom:14}}>
<div style={{fontSize:12,color:t.td,marginBottom:6}}>{historical.length} historical price difference{historical.length>1?'s':''} — this invoice predates the current price, no update will be applied</div>
{historical.map(x=>{
const item=x.item;const ig=ings?.find(z=>z.id===item.matched_ingredient_id);
const isCW2=!!item.isCatchWeight;
const curCp=ig?.cp??item.old_price;
const curCq=ig?.cq??1;
const displayOld=isCW2?(curCq>0?curCp/curCq:0):curCp;
const lbl=isCW2?`/${ig?.cu||'lb'}`:'/cs';
return<div key={`ph-${x.idx}`} style={{fontSize:11,color:t.td,padding:'3px 0'}}>
<span>{ig?.n||item.matched_name}{isCW2&&<span style={{fontSize:8,color:t.bl,marginLeft:4}}>catch-wt</span>}</span>
<span style={{fontFamily:mn,marginLeft:8}}>${fmt$(item.new_price)}{lbl} on this invoice</span>
<span style={{fontFamily:mn,marginLeft:4,color:t.tm}}>→ current ${fmt$(displayOld)}{lbl}</span>
</div>;
})}
</div>}
</>;
})()}

{/* Line items table — mirrors Price Sheet layout */}
<div style={{overflow:"auto",marginBottom:14}}>
<table style={{width:"100%",borderCollapse:"collapse",minWidth:900}}>
<thead>
<tr style={{background:t.sf}}>
<PackGroupHeader sh={sh} colsBefore={7} colsAfter={3}/>
</tr>
<tr style={{background:t.sf}}>
<th style={{...sh,width:55}}>Item #</th>
<th style={sh}>Product</th>
<th style={{...sh,width:65}}>Brand</th>
<th style={{...sh,width:70}}>Dist</th>
<th style={{...sh,width:70}}>Cost Cat</th>
<th style={{...sh,width:75}}>Category</th>
<th style={{...sh,width:40}}>Ord</th>
<PackSubHeaders sh={sh}/>
<th style={{...sh,width:65}}>Case $</th>
<th style={{...sh,width:65}}>Unit Cost</th>
<th style={{...sh,width:80}}>Match</th>
</tr>
</thead>
<tbody>{matched.map((item,i)=>{
const flag=item.price_changed?priceFlag(item.pct_change):null;
const isZero=lineZeroAmount(item);
const ig=item.matched_ingredient_id?ings?.find(x=>x.id===item.matched_ingredient_id):null;
const pack=getLinePack(item,i);
const isCW=item.catch_weight!=null;
const casePrice=isCW?item.amount:(item.amount-(item.line_tax||0))/(item.qty_shipped||1);
const uc=isCW?item.unit_price:unitCost(casePrice,pack.qty,pack.size);
const diffs=ig?getDiffs(item,ig,pack):null;
const showComparison=!!diffs;
const ec=ig?(ig.ec||'Food'):(newItemEC[i]||suggestEC(item.description,item.category));
const isNewItem=!ig&&COGS_SET.has(ec);
const isUnresolved=isNewItem&&!skippedLines.has(i)&&!approvedNewItems;
const invoicePack=parsePackSize(item.pack_size,item.pack_qty);
const packMismatch=ig&&invoicePack&&invoicePack.ppc>1&&(ig.ppc||1)>1&&invoicePack.ppc!==(ig.ppc||1);
const rowBg=isUnresolved?'rgba(59,130,246,0.06)':isZero?"rgba(200,55,45,0.08)":flag==='red'?"rgba(200,55,45,0.06)":flag==='yellow'?"rgba(212,168,36,0.06)":"transparent";
const rowBorder=isUnresolved?`3px solid ${t.bl}`:isZero?`3px solid ${t.ac}`:flag?`3px solid ${flag==='red'?t.ac:t.yl}`:'3px solid transparent';

return(<>
{/* Main invoice row */}
<tr key={i} ref={isUnresolved?el=>{unresolvedRefs.current[i]=el;}:null} style={{borderBottom:showComparison?'none':`1px solid ${t.bd}`,background:rowBg,borderLeft:rowBorder}}>
{/* Item # */}
<td style={cs}>{isZero?<span title="$0 amount — review" style={{color:t.ac,fontWeight:700}}>⚠️ </span>:flag==='red'?<span style={{color:t.ac,fontWeight:700}}>🔴 </span>:flag==='yellow'?<span style={{color:t.yl}}>🟡 </span>:null}<span style={{fontSize:10,color:t.td}}>{item.item_number||"—"}</span></td>

{/* Product */}
<td style={{...cs,fontSize:11,fontWeight:500}}>{item.description}{isZero&&<div style={{fontSize:8,color:t.ac,marginTop:1}}>$0 amount — review before saving</div>}{packMismatch&&<div style={{fontSize:8,color:t.yl,marginTop:1}}>⚠ Pack mismatch: invoice {invoicePack.ppc}pk vs catalog {ig.ppc}pk</div>}{item.pack_qty_flag&&<div style={{fontSize:8,color:t.bl,marginTop:1}}>{item.pack_qty_flag}</div>}</td>

{/* Brand */}
<td style={{...cs,color:t.tm,fontSize:10}}>{item.brand||"—"}</td>

{/* Dist */}
<td style={{...cs,fontSize:10}}>{extracted.vendor||"—"}</td>

{/* Cost Cat */}
<td style={cs}>{ig?<span style={{fontSize:9,padding:"1px 5px",borderRadius:3,background:"rgba(59,130,246,0.1)",color:t.bl}}>{ec==='Beverage'?'NA Bev':ec}</span>:<select value={ec} onChange={e=>setNewItemEC(p=>({...p,[i]:e.target.value}))} style={{...si,padding:"1px 3px",fontSize:9,width:65}}>{EC_OPTS.map(c=><option key={c} value={c}>{c==='Beverage'?'NA Bev':c}</option>)}</select>}</td>
{/* Ingredient Category — only for lines that will auto-create */}
<td style={cs}>{(isNewItem||(ig&&!ig.categoryId))&&setNewItemCat&&ingCatOptions.length>0?(()=>{const aiCat=item.ingredient_category||'';const curCat=newItemCat?.[i]||aiCat||(ig?.cat)||'';const lowConf=!newItemCat?.[i]&&!ig?.categoryId&&typeof item.ingredient_category_confidence==='number'&&item.ingredient_category_confidence<0.7;return<select value={curCat} onChange={e=>setNewItemCat?.(p=>({...p,[i]:e.target.value}))} style={{...si,padding:"1px 3px",fontSize:9,width:75,...(lowConf?{borderColor:t.yl,background:'rgba(212,168,36,0.15)'}:{})}}>{!curCat&&<option value="">—</option>}{ingCatOptions.map(c=><option key={c} value={c}>{c}</option>)}</select>;})():ig?<span style={{fontSize:9,color:t.td}}>{ig.cat||'—'}</span>:null}</td>

{/* Ordered (invoice qty) */}
<td style={{...csm,cursor:'pointer',position:'relative'}} onClick={()=>{if(!editingCell||editingCell.row!==i||editingCell.field!=='qty_shipped')startEdit(i,'qty_shipped',item.qty_shipped);}}>
{editingCell?.row===i&&editingCell?.field==='qty_shipped'
?<input autoFocus type="number" value={editValue} onChange={e=>setEditValue(e.target.value)} onBlur={()=>commitEdit(i,'qty_shipped')} onKeyDown={e=>handleKeyDown(e,i,'qty_shipped')} style={{...si,width:40,padding:'2px 4px',fontSize:10,fontFamily:mn,textAlign:'right'}}/>
:<>{item.qty_shipped}{isEdited(i,'qty_shipped')&&<span title={`OCR: ${originalVal(i,'qty_shipped')}`} style={{fontSize:7,color:t.bl,marginLeft:2,verticalAlign:'super'}}>*</span>}</>}
</td>

{isCW
?<><td style={{...cs,fontSize:9}}>—</td><td style={{...csm,fontSize:11,whiteSpace:'nowrap'}}>{item.catch_weight} {ig?.cu||'LB'}</td></>
:<PackCells qty={pack.qty} type={pack.type} size={pack.size} unit={pack.unit} mn={mn} sd={cs}/>}

{/* Case $ / Unit Cost */}
<td style={{...csm,fontWeight:600}}>${fmt$(casePrice)}{isCW&&<div style={{fontSize:8,color:t.td,fontWeight:400}}>{item.qty_shipped} cs</div>}</td>
<td style={{...csm,color:t.td}}>{uc!=null?'$'+uc.toFixed(uc<1?4:2)+(isCW?'/'+(item.unit||'lb').toLowerCase():''):"—"}</td>

{/* Match */}
<td style={{...cs,position:'relative'}}>{ig?<div style={{display:'flex',alignItems:'center',gap:3}}>
<span style={{fontSize:9,color:t.gn,fontWeight:600}} title={item.matched_name}>✓</span>
<button onClick={()=>{setReassignRow(reassignRow?.idx===i&&reassignRow?.source==='table'?null:{idx:i,source:'table'});setReassignSearch('');}} style={{background:'rgba(59,130,246,0.08)',border:`1px solid ${t.bl}`,color:t.bl,cursor:'pointer',fontSize:9,fontFamily:"'DM Sans',sans-serif",padding:'1px 6px',borderRadius:8}}>Change</button>
{item.price_changed&&<label style={{display:'flex',alignItems:'center',gap:2,fontSize:9,color:t.tm,cursor:'pointer'}}><input type="checkbox" checked={priceUpdates[item.matched_ingredient_id]||false} onChange={e=>setPriceUpdates(p=>({...p,[item.matched_ingredient_id]:e.target.checked}))} style={{width:12,height:12}}/>Upd</label>}
</div>:<div>
<div style={{display:'flex',alignItems:'center',gap:3,marginBottom:item.match_candidates?.length?3:0}}>
{isNewItem&&!skippedLines.has(i)&&<span style={{fontSize:8,padding:'1px 5px',borderRadius:3,background:'rgba(59,130,246,0.1)',color:t.bl,fontWeight:600}}>NEW</span>}
{skippedLines.has(i)&&<span style={{fontSize:8,padding:'1px 5px',borderRadius:3,background:'rgba(139,143,158,0.12)',color:t.td,fontWeight:600}}>SKIP</span>}
<button onClick={()=>{setReassignRow(reassignRow?.idx===i&&reassignRow?.source==='table'?null:{idx:i,source:'table'});setReassignSearch('');}} style={{background:'rgba(59,130,246,0.08)',border:`1px solid ${t.bl}`,color:t.bl,cursor:'pointer',fontSize:9,fontFamily:"'DM Sans',sans-serif",padding:'1px 6px',borderRadius:8}}>Match</button>
{isNewItem&&!skippedLines.has(i)&&<button onClick={()=>setSkippedLines(p=>{const s=new Set(p);s.add(i);return s;})} style={{background:'transparent',border:`1px solid ${t.bd}`,color:t.td,cursor:'pointer',fontSize:9,fontFamily:"'DM Sans',sans-serif",padding:'1px 6px',borderRadius:8}}>Skip</button>}
{skippedLines.has(i)&&<button onClick={()=>setSkippedLines(p=>{const s=new Set(p);s.delete(i);return s;})} style={{background:'transparent',border:`1px solid ${t.bd}`,color:t.td,cursor:'pointer',fontSize:9,fontFamily:"'DM Sans',sans-serif",padding:'1px 6px',borderRadius:8}}>Undo</button>}
</div>
{item.match_candidates?.length>0&&<div style={{fontSize:9,color:t.td}}>{item.match_candidates.slice(0,2).map((c,ci)=><div key={ci} style={{cursor:'pointer',color:t.bl,marginTop:1}} onClick={()=>{const ig2=(ings||[]).find(x=>x.id===c.ingredient_id);if(ig2)reassignLine(i,ig2);}} title={`${Math.round(c.score*100)}% match`}>↳ {c.name}?</div>)}</div>}
</div>}
{/* Reassign dropdown */}
{reassignRow?.idx===i&&reassignRow?.source==='table'&&<div ref={reassignRef} style={{position:'absolute',top:'100%',right:0,zIndex:20,background:t.cd,border:`1px solid ${t.bd}`,borderRadius:8,padding:8,width:260,boxShadow:'0 4px 12px rgba(0,0,0,0.15)'}}>
<input autoFocus value={reassignSearch} onChange={e=>setReassignSearch(e.target.value)} placeholder="Search ingredients..." name="reassign-lookup" autoComplete="chrome-off" data-1p-ignore data-lpignore="true" style={{...si,width:'100%',marginBottom:4,boxSizing:'border-box',fontSize:11}} onKeyDown={e=>{if(e.key==='Escape'){setReassignRow(null);setReassignSearch('');}}}/>
{reassignSearch.length>=2&&(()=>{const results=(ings||[]).filter(ig2=>ig2.active!==false&&matchTokensAny([ig2.n,ig2.b||'',ig2.cat||''],reassignSearch)).slice(0,8);return results.length?<div style={{maxHeight:180,overflow:'auto'}}>{results.map(ig2=><div key={ig2.id} onClick={()=>reassignLine(i,ig2)} style={{padding:'5px 8px',cursor:'pointer',borderBottom:`1px solid ${t.bd}`,fontSize:11}} onMouseOver={e=>e.currentTarget.style.background=t.sf} onMouseOut={e=>e.currentTarget.style.background='transparent'}>
<div style={{fontWeight:500}}>{ig2.n}</div>
<div style={{fontSize:9,color:t.td}}>{ig2.b||'—'} · {ig2.cat||'—'} · {ig2.dist||'—'}</div>
</div>)}</div>:<div style={{fontSize:10,color:t.td,padding:4}}>No matches</div>;})()}
{reassignSearch.length<2&&<div style={{fontSize:10,color:t.td,padding:4}}>Type 2+ chars to search</div>}
</div>}
</td>
</tr>

{/* Comparison row — only when price or pack differs */}
{showComparison&&<tr key={`cmp-${i}`} style={{borderBottom:`1px solid ${t.bd}`,background:rowBg,borderLeft:rowBorder}}>
<td style={csd}/>
<td style={{...csd,fontSize:10,fontStyle:'italic'}} colSpan={3}>{ig.n}</td>
<td style={csd}><span style={{fontSize:8,color:t.td}}>{ig.ec||'Food'}</span></td>
<td style={csd}><span style={{fontSize:8,color:t.td}}>{ig.cat||'—'}</span></td>
<td style={csd}/>
<PackCells qty={ig.ppc||1} type={ig.pt||'CS'} size={ig.spp} unit={ig.cu} mn={mn} sd={csd} dimmed/>
<td style={{...csd,fontFamily:mn}}>{diffs.price?<><span style={{textDecoration:'line-through'}}>${fmt$(ig.cp||0)}</span><span style={{color:item.new_price>(ig.cp||0)?t.ac:t.gn,marginLeft:3,fontSize:9}}>({(item.pct_change>0?'+':'')+item.pct_change.toFixed(1)}%)</span></>:`$${fmt$(ig.cp||0)}`}</td>
<td style={{...csd,fontFamily:mn}}>{ig.cq>0?'$'+(ig.cp/ig.cq).toFixed(ig.cp/ig.cq<1?4:2):"—"}</td>
<td style={csd}><span style={{fontSize:8,color:t.td}}>current</span></td>
</tr>}
</>);})}</tbody>
</table></div>

{/* Edit summary */}
{Object.keys(lineEdits).length>0&&<div style={{fontSize:10,color:t.td,marginBottom:8,fontStyle:'italic'}}>
{Object.values(lineEdits).reduce((n,row)=>n+Object.values(row).filter(e=>e.original!==e.current).length,0)} field(s) edited from OCR values
</div>}

{/* Non-line-item charges */}
{(extras>0||discount>0)&&<div style={{fontSize:11,color:t.td,marginTop:8,marginBottom:8}}>
{extracted.tax>0?`Tax: $${fmt$(extracted.tax)}`:''}
{extracted.tax>0&&(extracted.freight>0||extracted.bottle_deposit>0||discount>0)?' • ':''}
{extracted.freight>0?`Freight: $${fmt$(extracted.freight)}`:''}
{extracted.freight>0&&(extracted.bottle_deposit>0||discount>0)?' • ':''}
{extracted.bottle_deposit>0?`Bottle Deposit: $${fmt$(extracted.bottle_deposit)}`:''}
{extracted.bottle_deposit>0&&discount>0?' • ':''}
{discount>0?<span style={{color:t.gn}}>Discount: −${fmt$(discount)}</span>:''}
</div>}

{/* Category breakdown */}
{extracted.category_subtotals?.length>0&&<div style={{marginBottom:14}}>
<div style={{fontSize:11,color:t.tm,textTransform:"uppercase",marginBottom:6}}>Category Breakdown</div>
<div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:6}}>
{extracted.category_subtotals.map(c=>
<div key={c.category} style={{display:"flex",justifyContent:"space-between",padding:"4px 8px",background:t.sf,borderRadius:4}}>
<span style={{fontSize:11}}>{c.category}</span>
<span style={{fontFamily:mn,fontSize:11,fontWeight:500}}>${fmt$(c.subtotal)}</span>
</div>)}
</div>
</div>}

{/* Totals */}
<div style={{display:"flex",justifyContent:"flex-end",gap:16,marginBottom:14}}>
<div style={{textAlign:"right"}}>
{extracted.subtotal&&<div style={{fontSize:11,color:t.td}}>Subtotal: <span style={{fontFamily:mn}}>${fmt$(extracted.subtotal)}</span></div>}
{extracted.tax>0&&<div style={{fontSize:11,color:t.td}}>Tax: <span style={{fontFamily:mn}}>${fmt$(extracted.tax)}</span></div>}
{extracted.freight>0&&<div style={{fontSize:11,color:t.td}}>Freight/Fuel: <span style={{fontFamily:mn}}>${fmt$(extracted.freight)}</span></div>}
{extracted.bottle_deposit>0&&<div style={{fontSize:11,color:t.td}}>Bottle Deposit: <span style={{fontFamily:mn}}>${fmt$(extracted.bottle_deposit)}</span></div>}
{discount>0&&<div style={{fontSize:11,color:t.gn}}>Discount: <span style={{fontFamily:mn}}>−${fmt$(discount)}</span></div>}
<div style={{fontSize:14,fontWeight:700,marginTop:4}}>Total: <span style={{fontFamily:mn,color:t.gn}}>${fmt$(activeTotal)}</span></div>
</div>
</div>

{/* New items approval */}
{hasUnresolved&&onConfirm&&<div style={{background:'rgba(59,130,246,0.05)',border:`1px solid ${t.bl}`,borderRadius:8,padding:'10px 14px',marginBottom:10}}>
<div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:10}}>
<span style={{fontSize:11,color:t.bl,fontWeight:600,cursor:'pointer'}} onClick={()=>{const first=unresolvedLines[0];const el=unresolvedRefs.current[first?.idx];if(el)el.scrollIntoView({behavior:'smooth',block:'center'});}}>{unresolvedLines.length} unmatched: {unresolvedLines.slice(0,3).map(({item})=>item.description||'(no name)').join(', ')}{unresolvedLines.length>3?` +${unresolvedLines.length-3} more`:''}</span>
<button onClick={()=>setApprovedNewItems(true)} style={{...sb,background:t.bl,padding:'6px 14px',fontSize:11,flexShrink:0}}>Create all new items</button>
</div>
<div style={{fontSize:10,color:t.td,marginTop:4}}>Click names to scroll — or link, skip, or create each to unblock confirm</div>
</div>}
{!hasUnresolved&&approvedNewItems&&onConfirm&&<div style={{display:'flex',alignItems:'center',justifyContent:'flex-end',gap:10,marginBottom:10}}>
<span style={{fontSize:11,color:t.gn,fontWeight:600}}>All lines resolved ✓</span>
</div>}

{/* Actions */}
{(onConfirm||onCancel)&&<div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
{onReject&&<button onClick={onReject} style={{...sb,background:'rgba(200,55,45,0.08)',color:t.ac,border:`1px solid ${t.ac}`}}>Reject</button>}
{onCancel&&<button onClick={onCancel} style={{...sb,background:t.sf,color:t.tm,border:`1px solid ${t.bd}`}}>{queueMode?'Skip':'Cancel'}</button>}
{!queueMode&&onDefer&&<button onClick={onDefer} style={{...sb,background:t.bl,padding:"10px 16px"}}>Save for Later</button>}
{onConfirm&&<><button ref={confirmBtnRef} onClick={doConfirm} disabled={confirmBlocked||!allNewApproved||isSaving} style={{...sb,background:(confirmBlocked||!allNewApproved||isSaving)?'#888':t.gn,padding:"10px 24px",opacity:(confirmBlocked||!allNewApproved||isSaving)?0.5:1,cursor:(confirmBlocked||!allNewApproved||isSaving)?'not-allowed':'pointer'}}>
{isSaving?'Saving...':confirmBlocked?'Reconcile to confirm':!allNewApproved?`Resolve ${unresolvedLines.length} unmatched`:queueMode?(`Confirm & Next${priceChanges.filter(i=>priceUpdates[i.matched_ingredient_id]).length>0?` (${priceChanges.filter(i=>priceUpdates[i.matched_ingredient_id]).length} price upd)`:''}`):(`Confirm & Save${priceChanges.filter(i=>priceUpdates[i.matched_ingredient_id]).length>0?` (${priceChanges.filter(i=>priceUpdates[i.matched_ingredient_id]).length} price update${priceChanges.filter(i=>priceUpdates[i.matched_ingredient_id]).length>1?"s":""})`:""}`)}</button>
{queueMode&&!isSaving&&<span style={{fontSize:9,color:t.td,alignSelf:'center'}}>⌘Enter</span>}</>}
</div>}
{error&&<div style={{fontSize:11,color:t.ac,marginTop:10}}>{error}</div>}

{/* Ingredient edit modal from price change row */}
{editIngItem&&<Modal open={true} onClose={()=>setEditIngItem(null)} width={680} t={t}>
<div style={{marginBottom:12}}>
<div style={{fontSize:16,fontWeight:700}}>Edit Ingredient</div>
<div style={{fontSize:12,color:t.td,marginTop:2}}>{editIngItem.ig.n} — {editIngItem.ig.b||''}</div>
<div style={{background:'rgba(212,168,36,0.08)',border:`1px solid ${t.yl}`,borderRadius:6,padding:'8px 12px',marginTop:8,fontSize:11}}>
<span style={{color:t.yl,fontWeight:600}}>Invoice:</span> <span style={{fontFamily:mn}}>${fmt$(editIngItem.invoiceItem.new_price)}/cs</span>
<span style={{color:t.td,marginLeft:8}}>was <span style={{fontFamily:mn}}>${fmt$(editIngItem.invoiceItem.old_price)}/cs</span></span>
<span style={{color:t.td,marginLeft:8}}>({editIngItem.invoiceItem.pct_change>0?'+':''}{editIngItem.invoiceItem.pct_change.toFixed(1)}%)</span>
</div>
</div>
<IngredientForm mode="edit" initial={editIngItem.ig} onSave={(data)=>{
  const oldIg=ings.find(x=>x.id===editIngItem.ig.id);
  if(oldIg){
    const ppc=parseFloat(data.ppc)||1,spp=parseFloat(data.spp)||0;
    data.cq=ppc*spp;
    if(Math.abs(oldIg.cp-(data.cp||0))>0.01&&save?.logPriceChange){save.logPriceChange(orgId,uid,oldIg.id,oldIg.cp,data.cp,'invoice');}
    if(setIngs)setIngs(p=>p.map(i=>i.id===oldIg.id?{...i,...data,prevCp:i.cp!==data.cp?i.cp:i.prevCp,updatedAt:new Date().toISOString()}:i));
    save.updateIngredient(oldIg.id,data);
  }
  setEditIngItem(null);
}} onCancel={()=>setEditIngItem(null)} categories={ings?[...new Set(ings.map(i=>i.cat))].sort():[]} vendors={settings?.vendors?.sort()||[]} t={t} si={si} sb={sb} mn={mn}/>
</Modal>}
</div>);
}
